import React, {useEffect, useState} from 'react'
import {Button, Flex, Image, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import ProductRegistrationHeader from './partials/product-registration-header'
import ProductRegistrationStepper from './partials/product-registration-stepper'
import ProductRegistrationFields from './partials/forms/product-registration-fields'
import {useForm} from 'react-hook-form'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {
    convertDateToYYYYMMDD000000,
    formatDateToYYYYMMDD,
    generateGUID,
    convertYYYYMMDD000000ToDate,
} from '../../utils/utils'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {BRAND_SHORT_LABEL} from '../../brand'
import {useLocaleCountry, useLocaleLanguage} from '../../hooks/use-locale-custom'
import routingObject from '../../utils/static-routes'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import ProductRegistrationModal from './partials/product-registration-modal'
import LoadingSpinner from '../../components/loading-spinner'
import {useTokenHelper} from '../../utils/token-helper'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {registration_event} from '../../custom-api/layers/product/registration'
import {useLocation} from 'react-router'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'

const ProductRegistrationForm = () => {
    const [product, setProduct] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const styles = useMultiStyleConfig('ProductRegistration', {variant: 'productSearch'})
    const formStyles = useMultiStyleConfig('ProductRegistrationForm')
    const form = useForm({
        shouldFocusError: false,
    })
    const customer = useCurrentCustomer()
    const navigate = useNavigation()
    const {fetchMCValidToken, fetchSAPCPIValidToken} = useTokenHelper()

    const [receiptBase64, setReceiptBase64] = useState('')
    const [receiptName, setReceiptName] = useState('')
    const [receiptMime, setReceiptMime] = useState('')
    const [receiptFile, setReceiptFile] = useState(null) // Add state for the file object
    const [error, setError] = useState(null)
    const [fileError, setFileError] = useState('') // State for file validation error
    const appOrigin = useOrigin(false)
    const {locale, site, buildUrl} = useMultiSite()
    const {currency} = useCurrency()
    const [path, setPath] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [lastFormData, setLastFormData] = useState(null)
    const lang = useLocaleLanguage()
    const country = useLocaleCountry()
    const [prepopulatedSerialNumber, setPrepopulatedSerialNumber] = useState(false)
    const [productRegistrationFailed, setProductRegistrationFailed] = useState(false)

    const location = useLocation()
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const intl = useIntl()
    const productRegistrationTitleMessage = defineMessage({
        id: 'product_registration.title',
        defaultMessage: 'Register your product',
    })

    const productRegistrationSubtitleMessage = defineMessage({
        id: 'product_registration.subtitle',
        defaultMessage: 'Register your product to unlock a world of benefits.',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(productRegistrationTitleMessage),
        description: intl.formatMessage(productRegistrationSubtitleMessage),
        url: currentPath,
    }

    const imageObjectSchemaMarkup = {
        '@type': 'ImageObject',
        contentUrl: product?.image?.link || '',
        creditText: `De'Longhi Appliances S.r.l`,
    }

    const uploadFileError = (
        <FormattedMessage
            id="product_registration.proof_of_purchase_error"
            defaultMessage="Proof of purchase is mandatory"
        />
    )
    const uploadFileSizeError = (
        <FormattedMessage
            id="product_registration.upload_file_size_error"
            defaultMessage="File too large. The maximum allowed size is 5MB."
        />
    )
    const uploadFileNameError = (
        <FormattedMessage
            id="product_registration.upload_file_name_error"
            defaultMessage="Invalid file name. Please remove dots or special characters."
        />
    )
    const queryParams =
        typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams()
    const productId = queryParams.get('code')
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const storedGuestId = basket?.c_gigyaGuestId

            const url = buildUrl(
                customer?.data?.isGuest && storedGuestId
                    ? `/registered-product/${storedGuestId}`
                    : '/account/registered-products',
            )
            setPath(`${appOrigin}${url}`)
        }
    }, [customer, appOrigin, buildUrl])

    useEffect(() => {
        // if there's no selectedProduct in localStorage, try to find it from the URL code and set it in localStorage
        // this happens for example when the user copies the URL of the product registration detail page and shares it, so when the recipient opens the link, we can still show the correct product info in the form
        // and send the correct payload to SAP CPI when registering the product.
        // Otherwise there would be an error in the API since there's no SKU in the payload, and the user would see a generic error message without knowing the root cause.

        const initProduct = async () => {
            if (typeof window === 'undefined') {
                return
            }

            const storedProduct = localStorage.getItem('selectedProduct')
            if (storedProduct) {
                const parsed = JSON.parse(storedProduct)
                const storedSku = parsed?.representedProduct?.id || parsed?.id

                // If no code in URL, or it matches the stored product, reuse it
                if (!productId || storedSku === productId) {
                    setProduct(parsed)
                    return
                }
                // If code changed and does not match stored product, fall through and refetch
                // this is to avoid issues such as navigating directly from ...?code=xxx to ...?code=yyy in the same session will now correctly update
            }

            if (!productId) {
                return
            }

            try {
                const token = await getTokenWhenReady()
                const fetchedProduct = await api.shopperSearch.productSearch({
                    headers: {Authorization: `Bearer ${token}`},
                    parameters: {
                        q: productId,
                        expand: ['represented_products', 'images', 'page_meta_tags'],
                        refine: ['c_enabledForReg=true'],
                        locale: locale.id,
                        site: site.id,
                        currency,
                        c_isProductReg: true,
                    },
                })

                const matchedHit = fetchedProduct?.hits?.find(
                    (hit) => hit?.representedProduct?.id === productId,
                )

                if (!matchedHit) {
                    return
                }

                setProduct(matchedHit)
                localStorage.setItem('selectedProduct', JSON.stringify(matchedHit))
            } catch (e) {
                console.error('Error retrieving product from URL code:', e)
            }
        }

        initProduct()
    }, [productId])

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        // Redirect as early as possible when we know gigyaId is missing.
        // We wait for the relevant data to be available to avoid redirecting during initial loading.
        // Such issue can happen when the user goes directly into product registration detail page
        // (e.g. -> https://www.delonghi.com/it-it/product-registration/user-detail?code=0132250063)
        // without going through the product selection page/login/register page, so the gigyaId has not been stored in the basket or customer data yet.

        const isGuest = customer?.data?.isGuest
        const isRegistered = customer?.data?.isRegistered

        // If we don't know customer type yet, do nothing.
        if (!isGuest && !isRegistered) {
            return
        }

        if (!productId) {
            return
        }

        if (isRegistered) {
            // Wait for the customer query to finish loading before checking gigyaId.
            // Right after login, isRegistered flips to true but the customer data fetch
            // hasn't completed yet, so c_gigyaId is momentarily undefined — without this
            // guard that would trigger a spurious redirect loop.
            if (customer.isLoading) {
                return
            }
            const customerId = customer?.data?.c_gigyaId
            if (!customerId || customerId === 'NO_GIGYA_ID') {
                navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/user-detail?code=${productId}`)
            }
            return
        }

        // Guest flow: rely on basket custom attribute
        if (isBasketLoading) {
            return
        }

        const guestId = basket?.c_gigyaGuestId
        if (!guestId || guestId === 'NO_GIGYA_ID') {
            navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/user-detail?code=${productId}`)
        }
    }, [
        customer?.data?.isGuest,
        customer?.data?.isRegistered,
        customer?.data?.c_gigyaId,
        customer.isLoading,
        basket?.c_gigyaGuestId,
        isBasketLoading,
        productId,
    ])

    useEffect(() => {
        const prepopulatedSerialNumber = localStorage.getItem('prepopulatedSerialNumber')
        if (prepopulatedSerialNumber) {
            form.setValue('serialNumber', prepopulatedSerialNumber)
            setPrepopulatedSerialNumber(true)
        }
    }, [form])

    useEffect(() => {
        const prepopulatedSerialNumber = localStorage.getItem('prepopulatedSerialNumber')
        if (prepopulatedSerialNumber) {
            form.setValue('serialNumber', prepopulatedSerialNumber)
            form.register('serialNumber', {disabled: true}) // Make the field read-only
        }
    }, [form])

    const {addLayer} = useGoogleTagManager()
    useEffect(() => {
        if (product) {
            addLayer(
                registration_event.bind(this, {
                    event: 'productRegistration',
                    eventCategory: 'afterSales',
                    eventAction: 'productRegistration',
                    eventLabel: 'detail',
                    productRegistration: {
                        products: [
                            {
                                id: product.representedProduct?.id, // product SKU
                                name: product.c_defaultName, // product name (English)
                                category: product.c_subCategoryDefaultName, // product category (English)
                            },
                        ],
                    },
                }),
            )
        }
    }, [product])

    // // Retrieve registered products with SAP CPI
    const handleProductRetrieve = async () => {
        setIsLoading(true)
        setError(null)
        try {
            const accessToken = await fetchSAPCPIValidToken()

            const gigyaId = customer?.data?.c_gigyaId || basket?.c_gigyaGuestId

            // For authenticated users, send the Commerce Cloud token and SFCC customer ID
            // so the server can verify the requested customerId belongs to the caller.
            const identityHeaders = {}
            const isAuthenticated = !!customer?.data?.c_gigyaId && !!customer?.data?.customerId
            if (isAuthenticated) {
                try {
                    const ccToken = await getTokenWhenReady()
                    identityHeaders['x-commercecloud-token'] = `Bearer ${ccToken}`
                    identityHeaders['customerid'] = customer.data.customerId
                } catch (tokenErr) {
                    console.warn(
                        'Could not fetch CC token for identity verification:',
                        tokenErr.message,
                    )
                }
            }

            const queryParams = new URLSearchParams({
                customerId: gigyaId,
                customerEmail: customer?.data?.email || '',
            })
            //Pass the email in the query params to log it in case of gigyaId is missing
            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/retrieve-registered-products-sap-cpi?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                        ...identityHeaders,
                    },
                },
            )

            const result = await response.json()
            if (result.success) {
                setProductRegistrationFailed(false)
                return result.products || []
            } else if (result.error && result.error === 'NoGigya') {
                setProductRegistrationFailed(true)
                //Make this an Text and translated message
                setError('Please retry the product registration flow')
            } else if (result.error?.toLowerCase().includes('products not found')) {
                // First registration: treat as empty list, no error
                setProductRegistrationFailed(false)
                return []
            } else {
                setError('Product retrieve failed: ' + result.error)
                return []
            }
        } catch (error) {
            setError('Error during product retrieve: ' + error.message)
            return []
        } finally {
            setIsLoading(false)
        }
    }

    // Register a product with SAP CPI
    const handleProductRegistration = async (dataWithIds) => {
        try {
            setIsLoading(true)
            if (productRegistrationFailed) {
                setError('Please retry the product registration flow')
                setIsLoading(false)
                return
            }
            const accessToken = await fetchSAPCPIValidToken()

            // Prepare the payload for product registration
            const registrationPayload = {
                ProductRegistrationForm: [
                    {
                        ProductRegistrationFormDetail: {
                            MessageID: generateGUID(),
                            Brand: BRAND_SHORT_LABEL,
                            RegistrationId: '', // Empty for new product registration
                            SerialNumber: dataWithIds?.serialNumber,
                            IsAutomaticProductRegistration: false,
                            SKU: product?.representedProduct?.id,
                            Language: lang.toUpperCase(),
                            ProductLocationCountry: country,
                            Productname: dataWithIds?.product?.productName,
                            Modelnumber: product?.representedProduct?.c_subModelIdentifier,
                            Primarycategory:
                                product?.c_defaultCategoryName || product?.primaryCategoryId,
                            Primarysubcategory: product?.c_subCategoryDefaultName,
                            Source: dataWithIds?.source.value,
                            Purchasedate: dataWithIds?.formattedPurchaseDate,
                            Registereddate: formatDateToYYYYMMDD(),
                            Registrationdeleted: false,
                            CustomerId: dataWithIds?.userId,
                            Receipts:
                                dataWithIds?.receiptMime &&
                                dataWithIds?.receiptName &&
                                dataWithIds?.receiptBase64
                                    ? [
                                          {
                                              Receipt_1: {
                                                  ReceiptMime: dataWithIds?.receiptMime,
                                                  ReceiptName: dataWithIds?.receiptName,
                                                  Receipt: dataWithIds?.receiptBase64,
                                              },
                                          },
                                      ]
                                    : [],
                        },
                    },
                ],
            }

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/push-product-registration-sap-cpi`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(registrationPayload),
                },
            )

            // If SAP CPI reports missing gigyaId (NoGigya), redirect user back to previous step
            if (response.status === 402) {
                navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/user-detail?code=${productId}`)
                return
            }

            const result = await response.json()
            if (result.success) {
                const registrationId = result.response?.Response?.RegistrationId?.[0] // Extract RegistrationId
                await triggerProductRegistrationEmailEvent(dataWithIds, registrationId)
            } else {
                if (result.error?.toLowerCase() === 'invalid file name format') {
                    setFileError(uploadFileNameError)
                } else {
                    // Provide more context about what went wrong
                    const errorMessage =
                        result.error ||
                        `Unexpected response format (HTTP ${response.status}): ${JSON.stringify(
                            result,
                        ).substring(0, 100)}`
                    setError('Product registration failed: ' + errorMessage)
                    console.error('Product registration failed:', {status: response.status, result})
                }
            }
        } catch (error) {
            console.error('Error during product registration:', error)
            setError('Error during product registration: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    // Trigger successful product registration email event to Marketing Cloud
    const triggerProductRegistrationEmailEvent = async (dataWithIds, registrationId) => {
        try {
            setIsLoading(true)
            const accessToken = await fetchMCValidToken()

            const triggerEmailPayload = {
                contactKey: dataWithIds?.userId,
                data: {
                    id_gigya: dataWithIds?.userId,
                    email: customer?.data?.email || basket?.customerInfo?.email,
                    sku: product?.representedProduct?.id,
                    serial_number: dataWithIds?.serialNumber || 'N/A',
                    product_name: dataWithIds?.product?.productName,
                    model_number: product?.representedProduct?.c_subModelIdentifier,
                    purchase_date: convertYYYYMMDD000000ToDate(dataWithIds?.formattedPurchaseDate),
                    registration_date: formatDateToYYYYMMDD(),
                    purchase_source: dataWithIds?.source?.value,
                    prod_registration_link: path,
                    registration_id: registrationId,
                    locale: locale?.id,
                    brand: BRAND_SHORT_LABEL,
                },
            }

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/trigger-product-registration-email-event-marketing-cloud`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(triggerEmailPayload),
                },
            )

            const result = await response.json()
            if (result.success) {
                navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/confirmation`)
            } else {
                setError('Email event triggering failed: ' + result.error)
            }
        } catch (error) {
            console.error('Error during product registration trigger email event:', error)
            setError('Error during product registration trigger email event: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    const onSubmit = async (data) => {
        const guest_gigyaId = basket?.c_gigyaGuestId

        // Custom validation for the file
        if (!receiptFile) {
            setFileError(uploadFileError)
            await handleFileValidationError()
            return
        }
        if (receiptFile.size > 5242880) {
            // Check if file size exceeds 5MB
            setFileError(uploadFileSizeError)
            await handleFileValidationError()
            return
        }

        // Clear error only if file is valid
        setFileError('')
        if (product) {
            await addLayer(
                registration_event.bind(this, {
                    event: 'productRegistration',
                    eventCategory: 'afterSales',
                    eventAction: 'productRegistration',
                    eventLabel: 'detail-clic-OK',
                    productRegistration: {
                        products: [
                            {
                                id: product.representedProduct?.id, // product SKU
                                name: product.c_defaultName, // product name (English)
                                category: product.c_subCategoryDefaultName, // product category (English)
                            },
                        ],
                    },
                }),
            )
        }

        try {
            // 1. Retrieve registered products
            const registeredProducts = await handleProductRetrieve()
            // 2. Check if product is already registered
            const alreadyRegistered = registeredProducts.some(
                (prod) => prod?.skuKut === product?.representedProduct?.id,
            )
            if (alreadyRegistered) {
                setLastFormData(data)
                setIsModalOpen(true)
                return
            }
            // 3. Continue registration
            const dataWithIds = {
                ...data,
                userId: customer?.data?.c_gigyaId || guest_gigyaId,
                product: product,
                receiptBase64,
                receiptName,
                receiptMime,
                receiptFile, // Include the file object
                formattedPurchaseDate: convertDateToYYYYMMDD000000(String(data?.purchasedate)),
            }
            await handleProductRegistration(dataWithIds)
        } catch (error) {
            console.error('Error:', error)
        }
    }

    const handleRegisterAnyway = async () => {
        setIsModalOpen(false)
        if (lastFormData) {
            const guest_gigyaId = basket?.c_gigyaGuestId
            const dataWithIds = {
                ...lastFormData,
                userId: customer?.data?.c_gigyaId || guest_gigyaId,
                product: product,
                receiptBase64,
                receiptName,
                receiptMime,
                receiptFile,
                formattedPurchaseDate: convertDateToYYYYMMDD000000(
                    String(lastFormData?.purchasedate),
                ),
            }
            await handleProductRegistration(dataWithIds)
        }
    }

    // Handle custom file validation errors
    const handleFileValidationError = async () => {
        if (product) {
            await addLayer(
                registration_event.bind(this, {
                    event: 'productRegistration',
                    eventCategory: 'afterSales',
                    eventAction: 'productRegistration',
                    eventLabel: 'detail-clic-KO',
                    productRegistration: {
                        products: [
                            {
                                id: product.representedProduct?.id, // product SKU
                                name: product.c_defaultName, // product name (English)
                                category: product.c_subCategoryDefaultName, // product category (English)
                            },
                        ],
                    },
                }),
            )
        }

        const uploadButton = document.querySelector('[data-testid="upload-file-button"]')
        if (uploadButton) {
            uploadButton.focus()
            uploadButton.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    }

    // Handle form field validation errors from react-hook-form
    const onFormValidationError = async (errors) => {
        if (product) {
            await addLayer(
                registration_event.bind(this, {
                    event: 'productRegistration',
                    eventCategory: 'afterSales',
                    eventAction: 'productRegistration',
                    eventLabel: 'detail-clic-KO',
                    productRegistration: {
                        products: [
                            {
                                id: product.representedProduct?.id, // product SKU
                                name: product.c_defaultName, // product name (English)
                                category: product.c_subCategoryDefaultName, // product category (English)
                            },
                        ],
                    },
                }),
            )
        }

        setTimeout(() => {
            // Check purchasedate first (top of form)
            if (errors?.purchasedate) {
                const element = document.getElementById('purchasedate')
                if (element) {
                    element.focus()
                    element.scrollIntoView({behavior: 'smooth', block: 'center'})
                    return
                }
            }

            // Check file error second (middle of form) - only if purchasedate is valid
            if (!receiptFile || receiptFile.size > 5242880) {
                const uploadButton = document.querySelector('[data-testid="upload-file-button"]')
                if (uploadButton) {
                    if (!receiptFile) {
                        setFileError(uploadFileError)
                    } else if (receiptFile.size > 5242880) {
                        setFileError(uploadFileSizeError)
                    }
                    uploadButton.focus()
                    uploadButton.scrollIntoView({behavior: 'smooth', block: 'center'})
                    return
                }
            }

            // Check source last (bottom of form) - only if purchasedate and file are valid
            if (errors?.source) {
                const element = document.getElementById('select-source')
                if (element) {
                    element.focus()
                    element.scrollIntoView({behavior: 'smooth', block: 'center'})
                }
            }
        }, 0)
    }

    // Don't render the form while we're still waiting to know whether the user has a valid
    // gigyaId — this prevents a flash of the form before the redirect effect fires.
    const isGuest = customer?.data?.isGuest
    const isRegistered = customer?.data?.isRegistered
    const isGigyaIdPending =
        productId &&
        ((!isGuest && !isRegistered) || // customer type not yet known
            (isRegistered && customer.isLoading) || // registered but customer data still fetching
            (isGuest && isBasketLoading)) // guest but basket still loading

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Helmet>
                <title>{intl.formatMessage(productRegistrationTitleMessage)}</title>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(imageObjectSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            {(isLoading || isGigyaIdPending) && <LoadingSpinner />}
            <Flex {...styles.headerStepperContainer}>
                <ProductRegistrationHeader productId={productId} index={3} variant={'product'} />
                <ProductRegistrationStepper index={3} />
            </Flex>
            <Flex {...styles.labelSearchInnerContainer}>
                <Text {...styles.labelSearchTitle} data-testid="title" as="h2">
                    <FormattedMessage
                        id="product_registration_form.add_purchase_details"
                        defaultMessage="Add purchase details"
                    />
                </Text>
            </Flex>
            <Flex {...formStyles.outerContainer}>
                <Flex {...formStyles.middleContainer}>
                    <Flex {...formStyles.innerContainer} w={'full'}>
                        <Image
                            display={['none', 'none', 'none', 'block']}
                            {...formStyles.image}
                            src={product?.image?.link}
                            alt={product?.productName}
                        />
                        <Flex {...formStyles.formContainer}>
                            <Image
                                display={['block', 'block', 'block', 'none']}
                                {...formStyles.image}
                                src={product?.image?.link}
                                alt={product?.productName}
                            />
                            <Flex {...formStyles.titleOuterContainer}>
                                <Text {...formStyles.titleText}>
                                    {product?.representedProduct?.c_variantIdentifier ||
                                        product?.name ||
                                        product?.productName}
                                </Text>
                                <Flex {...formStyles.titleInnerContainer}>
                                    <Text {...formStyles.registrationFormSubtitle}>
                                        <FormattedMessage
                                            id="product_registration_form.subtitle"
                                            defaultMessage="Registration Details"
                                        />
                                    </Text>
                                    <Text {...formStyles.registrationFormSubtitleDescription}>
                                        <FormattedMessage
                                            id="product_registration_form.description"
                                            defaultMessage="The purchase date cannot be changed, please delete the product registration and create a new one"
                                        />
                                    </Text>
                                </Flex>
                            </Flex>
                            {/* Error management */}
                            {error && (
                                <Text {...formStyles.errorText} color="red.500" mt={4}>
                                    {error}
                                </Text>
                            )}
                            <ProductRegistrationFields
                                form={form}
                                setReceiptBase64={setReceiptBase64}
                                setReceiptName={setReceiptName}
                                setReceiptMime={setReceiptMime}
                                setReceiptFile={setReceiptFile} // Pass the setter for the file object
                                disableSerialField={prepopulatedSerialNumber ? true : false}
                                fileError={fileError} // Pass the fileError state
                                setFileError={setFileError} // Pass the setter for fileError
                                clearCalendarIcon={true} //Pass a bool to hide the calendar icon on the calendar field
                            />
                        </Flex>
                    </Flex>
                </Flex>
                <Flex {...formStyles.buttonContainer}>
                    <Button
                        className="gigya-input-submit"
                        {...formStyles.button}
                        variant={'primary'}
                        onClick={form.handleSubmit(onSubmit, onFormValidationError)}
                        data-testid="button"
                    >
                        <Text {...formStyles.buttonText}>
                            <FormattedMessage
                                id="product_registration_form.complete_registration"
                                defaultMessage="Complete the registration"
                            />
                        </Text>
                    </Button>
                </Flex>
            </Flex>
            <ProductRegistrationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                width="100%"
                productName={product?.productName}
                onRegisterAnyway={handleRegisterAnyway}
            />
        </Flex>
    )
}

ProductRegistrationForm.propTypes = {
    product: PropTypes.object,
}

export default ProductRegistrationForm
